import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import config from "./config";

const APIUrl = config.API_BASE_URL + "/reset-password";

// ─── Design tokens (mirrors Login/Register) ───────────────────────────────────
const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    border: "#1e2d42",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    cyan:   "#00c8ff",
    green:  "#00e896",
    red:    "#ff4466",
    amber:  "#ffb340",
};
const FONT = "'DM Mono', monospace";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
  * { box-sizing: border-box; }
  .rp-input {
    width: 100%;
    background: ${C.bg};
    border: 1px solid ${C.border};
    color: ${C.text};
    font-family: ${FONT};
    font-size: 12px;
    padding: 10px 14px;
    border-radius: 4px;
    outline: none;
    transition: border-color .15s;
    letter-spacing: .3px;
  }
  .rp-input:focus { border-color: ${C.cyan}; }
  .rp-input::placeholder { color: ${C.meta}; }
  .rp-input:disabled { opacity: 0.5; cursor: not-allowed; }
  .rp-btn {
    width: 100%;
    padding: 11px;
    background: ${C.green}15;
    border: 1px solid ${C.green}40;
    color: ${C.green};
    font-family: ${FONT};
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 1.2px;
    border-radius: 4px;
    cursor: pointer;
    transition: background .15s, border-color .15s;
  }
  .rp-btn:hover:not(:disabled) {
    background: ${C.green}25;
    border-color: ${C.green}70;
  }
  .rp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  @keyframes orbPulse {
    0%,100% { transform: scale(1); opacity: .15; }
    50%      { transform: scale(3); opacity: 0; }
  }
`;

const Orb = ({ color, size = 8, speed = "3s" }) => (
<div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
<div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: color, animation: `orbPulse ${speed} ease-in-out infinite` }} />
<div style={{ position: "absolute", inset: "25%", borderRadius: "50%", background: color }} />
</div>
);

const Field = ({ label, children }) => (
<div style={{ marginBottom: 16 }}>
<label style={{ display: "block", fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 }}>
{label}
</label>
{children}
</div>
);

const ResetPassword = (props) => {
    // Pull token from query string: /reset-password?token=xxxxx
    const params        = new URLSearchParams(props.location.search);
    const resetToken    = params.get("token");

    const [password,        setPassword]        = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading,         setLoading]         = useState(false);
    const [success,         setSuccess]         = useState(false);
    const [errorMessage,    setErrorMessage]    = useState(null);

    // No token in URL — show invalid link state immediately
    const invalidLink = !resetToken;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!password.trim() || !confirmPassword.trim()) {
            setErrorMessage("Both fields are required");
            return;
        }
        if (password.length < 6) {
            setErrorMessage("Password must be at least 6 characters");
            return;
        }
        if (password !== confirmPassword) {
            setErrorMessage("Passwords do not match");
            return;
        }
        setErrorMessage(null);
        setLoading(true);
        axios.post(APIUrl,
            { token: resetToken, newPassword: password },
            { headers: { "x-api-key": config.API_KEY } }
            )
            .then(() => {
                setLoading(false);
                setSuccess(true);
                // Redirect to login after 2 seconds
                setTimeout(() => props.history.push("/Login"), 2000);
            })
            .catch((error) => {
                setLoading(false);
                if (error.response?.status === 400 || error.response?.status === 403) {
                    setErrorMessage(error.response.data?.message || "This reset link is invalid or has expired");
                } else {
                    setErrorMessage("Server error — please try again later");
                }
            });
    };

    return (
        <div style={{
        background:     C.bg,
            minHeight:      "100vh",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            padding:        "20px",
            fontFamily:     FONT,
    }}>
    <style>{CSS}</style>

    <div style={{ width: "100%", maxWidth: 360 }}>

    {/* ── Brand mark ─────────────────────────────────────────────────── */}
    <div style={{ textAlign: "center", marginBottom: 32 }}>
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
    <Orb color={C.green} size={10} speed="2.5s" />
        </div>
        <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 14, color: C.text, letterSpacing: "2px", marginBottom: 6 }}>
    BERBUL.AI
    </div>
    <div style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: "1px" }}>
    AUTONOMOUS TRADING · SPY 0DTE
    </div>
    </div>

    {/* ── Card ───────────────────────────────────────────────────────── */}
    <div style={{
        background:   C.bg1,
            border:       `1px solid ${C.border}`,
            borderRadius: 12,
            padding:      "32px 32px 28px",
    }}>

    {invalidLink ? (
        /* ── Invalid / missing token ───────────────────────────────── */
    <div style={{ textAlign: "center" }}>
    <div style={{
        width:        48,
            height:       48,
            borderRadius: "50%",
            background:   C.red + "18",
            border:       `1px solid ${C.red}40`,
            display:      "flex",
            alignItems:   "center",
            justifyContent: "center",
            margin:       "0 auto 20px",
    }}>
    <span style={{ fontSize: 20 }}>✕</span>
    </div>
    <div style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>
        Invalid link
    </div>
    <div style={{ fontFamily: FONT, fontSize: 11, color: C.body, fontWeight: 300, lineHeight: 1.7, marginBottom: 24 }}>
        This password reset link is missing or malformed. Please request a new one.
    </div>
    <Link to="/forgot-password" style={{ display: "block", fontFamily: FONT, fontSize: 10, color: C.amber, textDecoration: "none", fontWeight: 500, marginBottom: 12 }}>
        Request new reset link →
    </Link>
    <Link to="/Login" style={{ display: "block", fontFamily: FONT, fontSize: 10, color: C.meta, textDecoration: "none" }}>
    ← Back to sign in
    </Link>
    </div>

    ) : success ? (
        /* ── Success state ─────────────────────────────────────────── */
    <div style={{ textAlign: "center" }}>
    <div style={{
        width:        48,
            height:       48,
            borderRadius: "50%",
            background:   C.green + "18",
            border:       `1px solid ${C.green}40`,
            display:      "flex",
            alignItems:   "center",
            justifyContent: "center",
            margin:       "0 auto 20px",
    }}>
    <span style={{ fontSize: 20 }}>✓</span>
    </div>
    <div style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>
        Password updated
    </div>
    <div style={{ fontFamily: FONT, fontSize: 11, color: C.body, fontWeight: 300, lineHeight: 1.7 }}>
        Your password has been reset. Redirecting to sign in...
    </div>
    </div>

    ) : (
        /* ── Reset form ────────────────────────────────────────────── */
    <>
    {/* Card header */}
    <div style={{ marginBottom: 24 }}>
    <div style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>
        New password
    </div>
    <div style={{ fontFamily: FONT, fontSize: 11, color: C.body, fontWeight: 300 }}>
        Choose a new password for your account
    </div>
    </div>

    <Field label="New Password">
        <input
        className="rp-input"
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        disabled={loading}
        placeholder="min 6 characters"
        autoComplete="new-password"
            />
            </Field>

            <Field label="Confirm Password">
        <input
        className="rp-input"
        type="password"
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        disabled={loading}
        placeholder="repeat password"
        autoComplete="new-password"
        onKeyDown={e => e.key === "Enter" && handleSubmit(e)}
    />
    </Field>

    {/* Error */}
        {errorMessage && (
        <div style={{
            background:   C.red + "12",
                border:       `1px solid ${C.red}30`,
                borderRadius: 4,
                padding:      "9px 12px",
                marginBottom: 16,
                display:      "flex",
                alignItems:   "center",
                gap:          8,
        }}>
        <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.red, flexShrink: 0 }} />
        <span style={{ fontFamily: FONT, fontSize: 10, color: C.red }}>{errorMessage}</span>
        </div>
        )}

        {/* Submit */}
    <button className="rp-btn" onClick={handleSubmit} disabled={loading}>
        {loading ? "UPDATING..." : "SET NEW PASSWORD"}
        </button>

        {/* Back */}
        <div style={{ marginTop: 20, textAlign: "center" }}>
    <Link to="/Login" style={{ fontFamily: FONT, fontSize: 10, color: C.meta, textDecoration: "none" }}>
    ← Back to sign in
    </Link>
    </div>
    </>
    )}
    </div>
    </div>
    </div>
    );
};

export default ResetPassword;